import { useState, useMemo } from 'react';
import {
  X,
  Sparkles,
  Download,
  CheckCircle2,
  AlertCircle,
  Layers,
  Calendar,
  ShieldCheck,
  Loader2,
  FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import {
  parsePlatformOrderText,
  ParsedPlatformOrder,
} from '@/lib/platformFees.js';
import { api } from '@/lib/api.js';

interface OrderImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProjectCreated: (createdProject: any) => void;
}

const SAMPLE_FIVERR_ORDER = `Fiverr Order Confirmation
Order #FO928174829
Buyer: dkodaa
Gig: Full-Stack React & Node Application Development
Total Price: $1,250.00
Delivery Date: 2026-11-15
Revisions: 2
Order Requirements & Scope:
- Milestone 1: Authentication & database architecture
- Milestone 2: Responsive dashboard and client portal
- Milestone 3: Production deployment and handover
Link: https://www.fiverr.com/orders/FO928174829`;

const SAMPLE_UPWORK_CONTRACT = `Upwork Contract Offer
Contract ID: UP-4820194
Client: Nexus Health Solutions (sophia_k)
Job: Senior Full-Stack Engineer - Healthcare Analytics
Budget: $3,500.00
Hourly Limit: 30 hrs/week
Deadline: 2026-12-01
Escrow: Funded ($1,750 for Milestone 1)
Milestones:
1. Architecture setup & HIPAA compliant auth
2. FHIR API sync & patient telemetry dashboard
3. Automated test suite and deployment
Link: https://www.upwork.com/nx/wm/workroom/4820194`;

export function OrderImportModal({
  isOpen,
  onClose,
  onProjectCreated,
}: OrderImportModalProps) {
  if (!isOpen) return null;

  const [rawText, setRawText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successResult, setSuccessResult] = useState<any | null>(null);

  // Parse order in real time as user pastes or edits
  const parsed: ParsedPlatformOrder = useMemo(() => {
    return parsePlatformOrderText(rawText);
  }, [rawText]);

  const handleUseSample = (type: 'fiverr' | 'upwork') => {
    setRawText(type === 'fiverr' ? SAMPLE_FIVERR_ORDER : SAMPLE_UPWORK_CONTRACT);
    setError(null);
  };

  const handleImport = async () => {
    if (!rawText.trim()) {
      setError('Please paste raw text from your Fiverr or Upwork order confirmation.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        raw_text: rawText,
        platform: parsed.platform,
        order_id: parsed.order_id,
        client_name: parsed.client_name,
        client_username: parsed.client_username,
        project_name: parsed.project_name,
        gross_amount: parsed.gross_amount,
        revisions: parsed.revisions,
        escrow_status: parsed.escrow_status,
        hourly_limit: parsed.hourly_limit,
        deadline: parsed.deadline,
        platform_url: parsed.platform_url,
        milestones: parsed.milestones,
      };

      const res = await api.post<any>('/api/v1/projects/import-order', payload);
      const data = res?.data || res;
      setSuccessResult(data);
      if (onProjectCreated) {
        onProjectCreated(data.project || data);
      }
    } catch (err: any) {
      console.error('Failed to import order:', err);
      setError(err?.message || 'Failed to import platform order. Please check data format.');
    } finally {
      setLoading(false);
    }
  };

  const hasContent = rawText.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-150">
      <div className="bg-slate-900 border border-slate-700 w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Modal Header */}
        <div className="p-4 sm:p-5 border-b border-slate-800 flex items-center justify-between bg-slate-950/70">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-teal-500/10 border border-teal-500/30 text-teal-400">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold text-white">
                Smart 1-Click Multi-Channel Order Importer
              </h3>
              <p className="text-xs text-slate-400">
                Paste raw confirmation text from Fiverr or Upwork to extract and provision projects instantly
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-5 flex-1">
          {successResult ? (
            <div className="py-8 text-center space-y-4">
              <div className="w-14 h-14 bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <div>
                <h4 className="text-lg font-bold text-white">Project Successfully Provisioned!</h4>
                <p className="text-sm text-slate-300 mt-1 max-w-md mx-auto">
                  Created <strong>{successResult.project?.name || parsed.project_name}</strong> with client link and {parsed.milestones.length} deliverable milestone(s).
                </p>
              </div>

              <div className="inline-flex gap-2 pt-2">
                <Button
                  onClick={onClose}
                  className="bg-teal-600 hover:bg-teal-500 text-white text-xs px-5"
                >
                  View in Project Roster
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Quick Paste Area */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
                    <FileText className="w-4 h-4 text-teal-400" />
                    Paste Raw Platform Confirmation Text:
                  </label>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-slate-500 text-[11px]">Quick Samples:</span>
                    <button
                      type="button"
                      onClick={() => handleUseSample('fiverr')}
                      className="text-emerald-400 hover:text-emerald-300 underline font-mono text-[11px]"
                    >
                      Fiverr Sample
                    </button>
                    <span className="text-slate-700">|</span>
                    <button
                      type="button"
                      onClick={() => handleUseSample('upwork')}
                      className="text-green-400 hover:text-green-300 underline font-mono text-[11px]"
                    >
                      Upwork Sample
                    </button>
                  </div>
                </div>

                <textarea
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  placeholder="Paste raw order email or contract details here (e.g. Order #FO928174829, Buyer: dkodaa, Total Price: $1,250, Delivery Date: Nov 15)..."
                  rows={6}
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl p-3 text-xs font-mono text-slate-200 focus:outline-none focus:ring-1 focus:ring-teal-500 placeholder:text-slate-600 leading-relaxed"
                />
              </div>

              {/* Error banner */}
              {error && (
                <div className="p-3 rounded-xl border border-rose-500/40 bg-rose-950/40 text-rose-300 text-xs flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* Real-time Extracted Data Preview */}
              {hasContent && (
                <div className="space-y-3 pt-2 border-t border-slate-800">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-teal-400 flex items-center gap-1.5">
                      <ShieldCheck className="w-4 h-4" />
                      Live Extracted Order Metadata
                    </h4>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400">Detected Channel:</span>
                      <Badge
                        className={
                          parsed.platform === 'fiverr'
                            ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                            : parsed.platform === 'upwork'
                            ? 'bg-green-500/20 text-green-300 border-green-500/40'
                            : 'bg-blue-500/20 text-blue-300 border-blue-500/40'
                        }
                      >
                        {parsed.platform.toUpperCase()} ({parsed.platform === 'fiverr' ? '20% Cut' : parsed.platform === 'upwork' ? '10% Cut' : '0% Cut'})
                      </Badge>
                    </div>
                  </div>

                  {/* Summary Metric Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800">
                      <div className="text-[11px] text-slate-400">Client / Buyer</div>
                      <div className="text-sm font-bold text-white truncate mt-0.5">
                        {parsed.client_name}
                      </div>
                      {parsed.client_username && (
                        <div className="text-[10px] text-teal-400 font-mono truncate">
                          @{parsed.client_username}
                        </div>
                      )}
                    </div>

                    <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800">
                      <div className="text-[11px] text-slate-400">Order / Contract ID</div>
                      <div className="text-sm font-bold text-white font-mono truncate mt-0.5">
                        {parsed.order_id || 'Auto-generated'}
                      </div>
                      <div className="text-[10px] text-slate-500">
                        {parsed.platform === 'fiverr' ? 'Fiverr Order' : 'Upwork Contract'}
                      </div>
                    </div>

                    <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800">
                      <div className="text-[11px] text-slate-400">Gross Contract Value</div>
                      <div className="text-sm font-bold text-white font-mono mt-0.5">
                        ${parsed.gross_amount.toLocaleString()}
                      </div>
                      <div className="text-[10px] text-amber-400/90 font-mono">
                        Platform Fee: -${parsed.platform_cut.toLocaleString()}
                      </div>
                    </div>

                    <div className="p-3 rounded-xl bg-teal-950/30 border border-teal-500/30">
                      <div className="text-[11px] text-teal-300 font-semibold">Net Agency Take-Home</div>
                      <div className="text-base font-extrabold text-teal-400 font-mono mt-0.5">
                        ${parsed.net_amount.toLocaleString()}
                      </div>
                      <div className="text-[10px] text-teal-300/80">
                        {parsed.margin_percent}% Net Margin
                      </div>
                    </div>
                  </div>

                  {/* Project Title & Deadline */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                    <div className="sm:col-span-2 p-2.5 rounded-lg bg-slate-950/40 border border-slate-800 flex items-center gap-2">
                      <Layers className="w-4 h-4 text-slate-400 shrink-0" />
                      <div className="truncate">
                        <span className="text-slate-400">Project Title: </span>
                        <span className="text-white font-semibold">{parsed.project_name}</span>
                      </div>
                    </div>

                    <div className="p-2.5 rounded-lg bg-slate-950/40 border border-slate-800 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-teal-400 shrink-0" />
                      <div>
                        <span className="text-slate-400">Deadline: </span>
                        <span className="text-white font-semibold font-mono">{parsed.deadline || 'None'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Extracted Milestones */}
                  <div className="space-y-1.5">
                    <div className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-teal-400" />
                      Parsed Deliverable Milestones ({parsed.milestones.length})
                    </div>
                    <div className="space-y-1 max-h-36 overflow-y-auto pr-1">
                      {parsed.milestones.map((m, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between text-xs p-2 rounded bg-slate-950/60 border border-slate-800 text-slate-300"
                        >
                          <span className="truncate">
                            <strong className="text-teal-400 font-mono mr-2">M{m.order || idx + 1}:</strong>
                            {m.title}
                          </span>
                          <span className="text-[10px] text-slate-500 font-mono ml-2 shrink-0">
                            {m.due_date || parsed.deadline || 'Pending'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal Footer */}
        {!successResult && (
          <div className="p-4 border-t border-slate-800 bg-slate-950/80 flex items-center justify-between gap-3">
            <div className="text-xs text-slate-400">
              {hasContent ? (
                <span>
                  Ready to provision <strong className="text-white">{parsed.project_name}</strong>
                </span>
              ) : (
                <span>Paste raw order text above to extract metadata</span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onClose}
                className="text-xs"
              >
                Cancel
              </Button>

              <Button
                type="button"
                size="sm"
                disabled={!hasContent || loading}
                onClick={handleImport}
                className="bg-teal-600 hover:bg-teal-500 text-white text-xs gap-1.5 shadow-md shadow-teal-900/30"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                {loading ? 'Provisioning...' : '1-Click Create Project & Milestones'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
